import _ from 'lodash';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useSearchParams } from 'react-router';
import deviceStore from '../../stores/deviceStore';
import Box from '../../uikit/Box';
import Frame from '../../uikit/Frame';
import { useMobxInstance } from '../../clientUtils/mobxUtil';
import Carousel, { CarouselProvider } from '../common/Carousel';
import IndexBriefCard from './IndexBriefCard';
import SectorBriefCard from './SectorBriefCard';
import Sp500BriefCard from './Sp500BriefCard';
import TreasuryBriefCard from './TreasuryBriefCard';
import GlobalBriefCard from './GlobalBriefCard';
import GoldBtcBriefCard from './GoldBtcBriefCard';
import TopGainerLoserBriefCard from './TopGainerLoserBriefCard';
import CurrencyBriefCard from './CurrencyBriefCard';
import BigTechBriefCard from './BigTechBriefCard';

class ViewStore {
    isHovered = false;

    constructor() {
        makeAutoObservable(this);
    }

    setHovered(hovered: boolean) {
        this.isHovered = hovered;
    }
}

const DailyBriefPage = observer(() => {
    const [searchParams, setSearchParams] = useSearchParams();
    const viewStore = useMobxInstance(ViewStore);

    const step = searchParams.get('step') || 'sp500';

    const stepItems = [
        {
            param: 'sp500',
            title: 'S&P500',
            description: '미국 주식시장의 전반적인 움직임을 확인해보세요.',
            component: <Sp500BriefCard />,
        },
        {
            param: 'treasury',
            title: '10년물 국채금리',
            description: '금리의 방향성을 파악하고 시장의 투자 심리를 살펴보세요.',
            component: <TreasuryBriefCard />,
        },
        {
            param: 'index',
            title: '주요 지수 비교',
            description: '대형주·기술주·중소형주·반도체주의 움직임을 비교해보세요.',
            component: <IndexBriefCard />,
        },
        {
            param: 'sector',
            title: '섹터별 흐름',
            description: '어떤 섹터가 강세·약세를 보였는지 특징을 파악해보세요.',
            component: <SectorBriefCard />,
        },
        {
            param: 'bigtech',
            title: '빅테크 및 주요 종목',
            description: '주요 빅테크 종목의 움직임을 확인해보세요.',
            component: <BigTechBriefCard />,
        },
        {
            param: 'top-gainer-loser',
            title: '주요 상승·하락 종목',
            description: 'S&P500 에서 가장 많이 상승·하락한 종목을 살펴보세요.',
            component: <TopGainerLoserBriefCard />,
        },
        {
            param: 'global',
            title: '주요국 지수 비교',
            description: '전 세계 주요 시장의 흐름을 확인해보세요.',
            component: <GlobalBriefCard />,
        },
        {
            param: 'currency',
            title: '환율',
            description: '원화 환율과 달러 인덱스를 확인해보세요.',
            component: <CurrencyBriefCard />,
        },
        {
            param: 'goldbtc',
            title: '비트코인과 금',
            description: '대표적인 위험자산과 안전자산의 움직임을 비교해보세요.',
            component: <GoldBtcBriefCard />,
        },
    ];

    const DEFAULT_STEP = 'sp500';
    const currentStepIndex =
        stepItems.findIndex((item) => item.param === step) ||
        stepItems.findIndex((item) => item.param === DEFAULT_STEP);
    const currentStepItem = stepItems[currentStepIndex];

    return (
        <Frame className="pb-0">
            <Frame.Title title={currentStepItem.title} />
            <Frame.Description>{currentStepItem.description}</Frame.Description>
            <CarouselProvider
                onSlideChange={(slideIndex) => {
                    const selectedStep = stepItems[slideIndex];
                    if (selectedStep && selectedStep.param !== step) {
                        setSearchParams(
                            (prev) => {
                                prev.set('step', selectedStep.param);
                                return prev;
                            },
                            {
                                preventScrollReset: true,
                                replace: true,
                            },
                        );
                    }
                }}
                currentSlideIndex={currentStepIndex}
            >
                <Box
                    className="relative flex grow flex-col px-0 py-0"
                    onMouseEnter={(e) => {
                        viewStore.setHovered(true);
                    }}
                    onMouseLeave={(e) => {
                        viewStore.setHovered(false);
                    }}
                >
                    <Carousel className="grow">
                        <Carousel.Container className="h-full">
                            {_.map(stepItems, (item) => (
                                <Carousel.Slide key={item.param} className="px-5">
                                    {item.component}
                                </Carousel.Slide>
                            ))}
                        </Carousel.Container>
                    </Carousel>

                    {/* 이전 버튼 (데스크탑 전용) */}
                    {deviceStore.hasHoverCapability && (
                        <Carousel.PrevCircleButton
                            isShow={viewStore.isHovered}
                            className="absolute top-1/2 -left-5.5 -translate-y-1/2"
                            size="lg"
                        />
                    )}

                    {/* 다음 버튼 (데스크탑 전용) */}
                    {deviceStore.hasHoverCapability && (
                        <Carousel.NextCircleButton
                            isShow={viewStore.isHovered}
                            className="absolute top-1/2 -right-5.5 -translate-y-1/2"
                            size="lg"
                        />
                    )}

                    <div className="py-5">
                        <Carousel.Dots />
                    </div>
                </Box>
            </CarouselProvider>
        </Frame>
    );
});

export default DailyBriefPage;
